import { HoverImageAction, PresetSection, PresetSectionItem } from '@wix/add-panel-common';
import { RefObject, useCallback, useEffect, useRef } from 'react';
import { useBoolean } from '../../../../../../hooks/useBoolean';
import { useOnError } from '../../../../../../OnErrorProvider';

interface UsePresetItemVideoParams {
  section: PresetSection;
  item: PresetSectionItem;
}

interface UsePresetItemVideo {
  isVideoPlaying: boolean;
  hasVideo: boolean;
  videoNodeRef: RefObject<HTMLVideoElement>;
  playVideo: () => void;
  pauseVideo: () => void;
}

export const usePresetItemVideo = ({ section, item }: UsePresetItemVideoParams): UsePresetItemVideo => {
  const { onError } = useOnError();

  const {
    additionalBehaviours: { hoverImageAction },
  } = section;
  const { preset } = item;

  const { value: isVideoPlaying, setTrue: startPlayingVideo, setFalse: stopPlayingVideo } = useBoolean(false);

  const hasVideo = Boolean(hoverImageAction === HoverImageAction.Video && preset.video);

  const videoNodeRef = useRef<HTMLVideoElement>(null);

  const playVideo = useCallback(async () => {
    const videoNode = videoNodeRef.current;
    if (!videoNode) {
      return;
    }

    startPlayingVideo();
  }, [startPlayingVideo]);

  const pauseVideo = useCallback(() => {
    const videoNode = videoNodeRef.current;
    if (!videoNode) {
      return;
    }

    stopPlayingVideo();
  }, [stopPlayingVideo]);

  useEffect(() => {
    const videoNode = videoNodeRef.current;
    if (!videoNode) {
      return;
    }

    const rafId = window.requestAnimationFrame(() => {
      videoNode.currentTime = 0;
      if (isVideoPlaying) {
        videoNode.muted = true;
        videoNode.play().catch(onError);
      } else {
        videoNode.pause();
      }
    });

    return () => {
      window.cancelAnimationFrame(rafId);
    };
  }, [isVideoPlaying, onError]);

  return {
    isVideoPlaying,
    hasVideo,
    videoNodeRef,
    playVideo,
    pauseVideo,
  };
};
